import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

import { createAxiosInstance } from "api/axiosConfig";
import { PATIENT_CONTROLER_URL_ID } from "api/Service";
import { EditLetterModal } from "features/communications/components/LettersSection/EditLetterModal";
import { EditSickCertModal } from "features/communications/components/SickCertsSection/EditSickCertModal";
import { usePatientSickCerts } from "features/communications/hooks/usePatientSickCerts";
import { useActiveDocumentType } from "features/documents/hooks/useActiveDocumentType";
import {
  DeleteConfirmModal,
  EditDocumentModal,
  UploadDocumentModal,
} from "features/documents/modals";
import { findCommunicationsSource } from "features/documents/utils/communicationsDocumentSource";
import { DISCHARGE_SUMMARIES_MENU_VALUE } from "features/documents/utils/dischargeSummary";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { usePatientLetters } from "hooks/usePatientLetters";
import {
  downloadDocument as downloadDocumentHelper,
  downloadDocumentsBulk,
  fetchDocumentForPreview,
} from "utils/documentDownload";
import { toRows } from "utils/paginatedResponse";

import { DocumentMenu } from "../components/DocumentMenu";
import { ChangeStatusModal } from "./components/ChangeStatusModal/ChangeStatusModal";
import { DeaspDetailsModal } from "./components/DeaspTable/DeaspDetailsModal/DeaspDetailsModal";
import { DeaspTable } from "./components/DeaspTable/DeaspTable";
import { DischargeSummariesTable } from "./components/DischargeSummariesTable/DischargeSummariesTable";
import { DischargeSummaryModal } from "./components/DischargeSummariesTable/DischargeSummaryModal";
import { DocumentList } from "./components/DocumentList/DocumentList";
import { DocumentPreviewModal } from "./components/DocumentPreviewModal/DocumentPreviewModal";
import { SendEmailModal } from "./components/SendEmailModal/SendEmailModal";
import { SendSmsModal } from "./components/SendSmsModal/SendSmsModal";
import { useAllDocumentsSources } from "./hooks/useAllDocumentsSources";
import { useDocumentFilters } from "./hooks/useDocumentFilters";
import { useDocumentPreview } from "./hooks/useDocumentPreview";
import { useDocumentSelection } from "./hooks/useDocumentSelection";
import { usePatientDocuments } from "./hooks/usePatientDocuments";
import { DOCUMENT_ROW_SOURCES } from "./utils/allDocumentsRows";
import { formatDocumentDate } from "./utils/documentFormatters";

// A document's own name, escaped: it reaches the print header, the tab and the filename offered on
// "Save as PDF", and it comes from a user-supplied upload.
const printTitleOf = (doc) =>
  String(doc?.fileName || doc?.name || "Document").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
  );

const ROWS_OPTIONS = [10, 20, 50, 100];

const PatientDocumentsPage = () => {
  const [patient, setPatient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const {
    documentTypes,
    isLoading: documentTypesLoading,
    activeType: menuItem,
    setActiveType: setMenuItem,
  } = useActiveDocumentType();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCommunicationsSource, setEditingCommunicationsSource] =
    useState(null);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(null);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [smsDocument, setSmsDocument] = useState(null);
  const [isPreviewEmailModalOpen, setIsPreviewEmailModalOpen] = useState(false);
  const [previewEmailDocument, setPreviewEmailDocument] = useState(null);
  /*
   * The viewers "All Documents" borrows from the sections that own those documents (KIN-2068).
   */
  const [openedDischargeSummary, setOpenedDischargeSummary] = useState(null);
  const [openedDeaspCertificate, setOpenedDeaspCertificate] = useState(null);

  const patientId = useCurrentPatientId();
  const tenant = localStorage.getItem("kinesin-tenant");

  const { data: staffInfo } = useStaffInfo();
  const currentUserStaffId = staffInfo?.staffMember?.id;

  /*
   * Only a sick cert or a letter can be edited from this tab (as opposed to the metadata-only
   * Edit every uploaded document already gets) — see findCommunicationsSource for why that is
   * resolved by matching each row's own document id against these lists rather than its
   * (tenant-renameable) document type name.
   */
  const { data: lettersData } = usePatientLetters(patientId, { rows: 100 });
  const { data: sickCertsData } = usePatientSickCerts(patientId, { rows: 100 });
  const patientLetters = useMemo(() => toRows(lettersData), [lettersData]);
  const patientSickCerts = useMemo(() => toRows(sickCertsData), [sickCertsData]);

  useEffect(() => {
    const fetchPatient = async () => {
      if (!patientId) return;

      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenant}${PATIENT_CONTROLER_URL_ID}${patientId}`
        );
        if (response?.data) {
          setPatient(response.data);
        }
      } catch (error) {
        console.error("Error fetching patient:", error);
        toast.error("Failed to load patient data");
      }
    };

    fetchPatient();
  }, [patientId, tenant]);

  /*
   * Sections backed by their own endpoint rather than by an uploaded document type. Their menu
   * values are not document types, so asking the documents endpoint for one would return nothing
   * — and asking at all is a wasted request.
   */
  const isOwnEndpointSection =
    menuItem === "DEASP" || menuItem === DISCHARGE_SUMMARIES_MENU_VALUE;

  const isAllSection = menuItem === "ALL";

  const {
    data: uploadedDocuments = [],
    isFetching: documentsFetching,
    refetch: refetchDocuments,
  } = usePatientDocuments(patientId, isAllSection ? undefined : menuItem, {
    enabled: !isOwnEndpointSection,
  });

  /*
   * "All" has to mean all (KIN-2068): the sections with their own endpoints are merged into this
   * list, so a patient whose only document is a hospital discharge summary is no longer told they
   * have none. Outside "All" the hook returns the uploads untouched and asks nothing extra.
   */
  const {
    rows: allDocuments,
    isLoading: documentsLoading,
    failedSourceLabels,
    retryFailedSources,
  } = useAllDocumentsSources({
    patientId,
    uploads: uploadedDocuments,
    isActive: isAllSection,
    isUploadsFetching: documentsFetching,
  });

  const {
    previewData,
    document: previewDocument,
    openPreview,
    closePreview,
  } = useDocumentPreview();

  const filters = useDocumentFilters(allDocuments);

  const totalFiltered = filters.documents.length;
  const totalPages = Math.ceil(totalFiltered / rowsPerPage) || 1;
  const pagedDocuments = filters.documents.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const selection = useDocumentSelection();

  const handleMenuClick = (value) => {
    setMenuItem(value);
    setCurrentPage(1);
    selection.deselectAll();
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadSuccess = () => {
    refetchDocuments();
  };

  const handleViewDocument = (documentId, patientId) => {
    openPreview(documentId, patientId);
  };

  /*
   * A row merged into "All" opens in the viewer its own section uses — the hospital's letter under
   * its own HSE stylesheet, the certificate in the DEASP details dialog — rather than in a second
   * rendering of the same document built for this list.
   */
  const handleViewSourceDocument = (row) => {
    if (row.source === DOCUMENT_ROW_SOURCES.dischargeSummary) {
      setOpenedDischargeSummary(row.sourceRow);

      return;
    }

    if (row.source === DOCUMENT_ROW_SOURCES.deasp) {
      setOpenedDeaspCertificate(row.sourceRow);
    }
  };

  const handleDeleteDocument = (document) => {
    setDeletingDocument(document);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    refetchDocuments();
    if (deletingDocument?.id) {
      selection.toggleSelection(deletingDocument.id, null, false);
    }
    setDeletingDocument(null);
  };

  const handlePrintDocument = async (document) => {
    const docPatientId =
      document?.patient?.id || document?.patientId || patientId;

    if (!document?.id || !docPatientId) {
      toast.error("Document information is missing");
      return;
    }

    try {
      const base64data = await fetchDocumentForPreview(
        document.id,
        docPatientId,
        tenant
      );

      if (base64data) {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
            <html>
              <head>
                <title>${printTitleOf(document)}</title>
                <style>
                  body { margin: 0; padding: 0; }
                  img { width: 100%; height: auto; }
                </style>
              </head>
              <body onload="window.print(); window.close();">
                <img src="${base64data}" />
              </body>
            </html>
          `);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.error("Error printing document:", error);
      toast.error("Failed to print document");
    }
  };

  const handleDownloadDocument = async (doc) => {
    const docPatientId = doc?.patient?.id || doc?.patientId || patientId;

    if (!doc?.id || !docPatientId || !doc?.fileName || !doc?.fileType) {
      toast.error(
        "Document information is missing (id, patientId, fileName, or fileType)"
      );
      return;
    }

    try {
      await downloadDocumentHelper({
        documentId: doc.id,
        patientId: docPatientId,
        tenant,
        fileName: doc.fileName,
        fileType: doc.fileType,
      });
      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const handleSendEmail = () => {
    if (selection.selectedCount === 0) {
      toast.error("Please select at least one document");
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleEmailModalClose = () => {
    setIsEmailModalOpen(false);
    selection.deselectAll();
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    refetchDocuments();
    setIsEditModalOpen(false);
    setEditingDocument(null);
  };

  const resolveCommunicationsSource = (document) =>
    findCommunicationsSource(document, {
      letters: patientLetters,
      sickCerts: patientSickCerts,
    });

  const handleEditCommunicationsDocument = (document) => {
    const source = resolveCommunicationsSource(document);
    if (!source) return;
    setEditingCommunicationsSource(source);
  };

  const handleCloseCommunicationsEdit = () => {
    setEditingCommunicationsSource(null);
  };

  const handleCommunicationsEditSaved = () => {
    refetchDocuments();
  };

  const handleChangeStatus = (document) => {
    setEditingDocument(document);
    setIsChangeStatusModalOpen(true);
  };

  const handleChangeStatusSuccess = () => {
    refetchDocuments();
    setIsChangeStatusModalOpen(false);
    setEditingDocument(null);
  };

  const handleBulkPrint = async () => {
    if (selection.selectedCount === 0) {
      toast.error("Please select at least one document");
      return;
    }

    const selectedDocs = selection.selectedArray;

    for (const doc of selectedDocs) {
      if (doc?.documentUrl) {
        await handlePrintDocument(doc);

        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  const handleBulkDownload = async () => {
    if (selection.selectedCount === 0) {
      toast.error("Please select at least one document");
      return;
    }

    const selectedDocs = selection.selectedArray;

    const docsToDownload = selectedDocs.map((doc) => ({
      id: doc.id,
      patientId: doc.patient?.id || doc.patientId || patientId,
      fileName: doc.fileName,
      fileType: doc.fileType,
    }));

    try {
      const results = await downloadDocumentsBulk(docsToDownload, tenant);
      if (results.success > 0) {
        toast.success(`Downloaded ${results.success} document(s)`);
      }
      if (results.failed > 0) {
        toast.error(`Failed to download ${results.failed} document(s)`);
        console.error("Download errors:", results.errors);
      }
    } catch (error) {
      console.error("Error during bulk download:", error);
      toast.error("Failed to download documents");
    }
  };

  const handleBulkChangeStatus = () => {
    if (selection.selectedCount === 0) {
      toast.error("Please select at least one document");
      return;
    }
    const firstDoc = selection.selectedArray[0];
    if (firstDoc) {
      setEditingDocument(firstDoc);
      setIsChangeStatusModalOpen(true);
    }
  };

  const handleReviewDocument = async (doc) => {
    if (!doc?.id) {
      toast.error("Invalid document");
      return;
    }

    if (doc.status?.toUpperCase() === "REVIEWED") {
      toast("Document is already reviewed");
      return;
    }

    try {
      const axiosInstance = createAxiosInstance();
      const payload = {
        ...doc,
        id: doc.id,
        status: "REVIEWED",
        reviewedById: currentUserStaffId,
      };

      await axiosInstance.post(`${tenant}/document`, payload);
      toast.success("Document marked as reviewed");
      refetchDocuments();
      closePreview();
    } catch (error) {
      console.error("Error reviewing document:", error);
      toast.error("Failed to review document");
    }
  };

  const getSmsInitialMessage = (doc) => {
    if (!doc) return "";
    const fileName = doc.fileName || doc.name || "Document";
    const dateUploaded = doc.created ? formatDocumentDate(doc.created) : "";
    const docType = doc.documentType?.replace(/_/g, " ") || "Document";

    return (
      `Document Details:\n` +
      `Name: ${fileName}\n` +
      `Type: ${docType}\n` +
      (dateUploaded ? `Date: ${dateUploaded}\n` : "") +
      `\nPlease review the attached document.`
    );
  };

  const handleSendEmailFromPreview = () => {
    if (!patient) {
      toast.error("Patient data not available");
      return;
    }
    setPreviewEmailDocument(previewDocument);
    setIsPreviewEmailModalOpen(true);
    closePreview();
  };

  return (
    <>
      <SimplePatientPageLayout
        sidebar={
          <DocumentMenu
            menuClick={handleMenuClick}
            documentTypes={documentTypes}
            isLoading={documentTypesLoading}
          />
        }
      >
        {menuItem === "DEASP" ? (
          <DeaspTable patientId={patientId} />
        ) : menuItem === DISCHARGE_SUMMARIES_MENU_VALUE ? (
          <DischargeSummariesTable patientId={patientId} />
        ) : (
        <DocumentList
          documents={pagedDocuments}
          totalDocuments={totalFiltered}
          isLoading={documentsLoading}
          filters={{
            searchQuery: filters.searchQuery,
            setSearch: (val) => { filters.setSearch(val); setCurrentPage(1); },
            statusFilter: filters.statusFilter,
            onStatusFilterChange: (val) => { filters.setStatus(val); setCurrentPage(1); },
            priorityFilter: filters.priorityFilter,
            onPriorityFilterChange: (val) => { filters.setPriority(val); setCurrentPage(1); },
            sortField: filters.sortField,
            sortDirection: filters.sortDirection,
            onSortChange: (field, dir) => { filters.setSort(field, dir); setCurrentPage(1); },
          }}
          selection={selection}
          onUploadClick={handleUploadClick}
          onView={handleViewDocument}
          onDelete={handleDeleteDocument}
          onPrint={handlePrintDocument}
          onDownload={handleDownloadDocument}
          onEdit={handleEditDocument}
          onEditCommunicationsDocument={handleEditCommunicationsDocument}
          isCommunicationsDocumentRow={(doc) => !!resolveCommunicationsSource(doc)}
          onChangeStatus={handleChangeStatus}
          onReview={handleReviewDocument}
          onBulkChangeStatus={handleBulkChangeStatus}
          onSendEmail={handleSendEmail}
          onBulkPrint={handleBulkPrint}
          onBulkDownload={handleBulkDownload}
          onViewSourceDocument={handleViewSourceDocument}
          failedSourceLabels={failedSourceLabels}
          onRetrySources={retryFailedSources}
          documentType={menuItem}
          showDocumentType={isAllSection}
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalFiltered}
          rowsPerPage={rowsPerPage}
          rowsOptions={ROWS_OPTIONS}
          rowsValue={rowsPerPage}
          onRowsChange={(val) => { setRowsPerPage(val); setCurrentPage(1); }}
          onPageChange={(page) => { setCurrentPage(page); selection.deselectAll(); }}
        />
        )}
      </SimplePatientPageLayout>

      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        patient={patient}
        documentType={menuItem}
        onSuccess={handleUploadSuccess}
      />

      <DocumentPreviewModal
        isOpen={!!previewData}
        onClose={closePreview}
        imageData={previewData}
        document={previewDocument}
        onSendEmail={handleSendEmailFromPreview}
        onReview={handleReviewDocument}
      />

      <DischargeSummaryModal
        summary={openedDischargeSummary}
        onClose={() => setOpenedDischargeSummary(null)}
      />

      <DeaspDetailsModal
        certificate={openedDeaspCertificate}
        onClose={() => setOpenedDeaspCertificate(null)}
      />

      <SendSmsModal
        isOpen={isSmsModalOpen}
        onClose={() => {
          setIsSmsModalOpen(false);
          setSmsDocument(null);
        }}
        patient={patient}
        initialMessage={getSmsInitialMessage(smsDocument)}
      />

      <SendEmailModal
        isOpen={isPreviewEmailModalOpen}
        onClose={() => {
          setIsPreviewEmailModalOpen(false);
          setPreviewEmailDocument(null);
        }}
        patient={patient}
        documentIds={previewEmailDocument?.id?.toString() || ""}
        documents={previewEmailDocument ? [previewEmailDocument] : []}
      />

      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={handleEmailModalClose}
        patient={patient}
        documentIds={selection.selectedIds.join(",")}
        documents={selection.selectedArray}
      />

      <EditDocumentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingDocument(null);
        }}
        document={editingDocument}
        patient={patient}
        onSuccess={handleEditSuccess}
      />

      <EditLetterModal
        open={editingCommunicationsSource?.kind === "LETTER"}
        letterId={
          editingCommunicationsSource?.kind === "LETTER"
            ? editingCommunicationsSource.id
            : null
        }
        patientId={patientId}
        onClose={handleCloseCommunicationsEdit}
        refetchList={handleCommunicationsEditSaved}
      />

      <EditSickCertModal
        open={editingCommunicationsSource?.kind === "SICK_CERT"}
        sickCertId={
          editingCommunicationsSource?.kind === "SICK_CERT"
            ? editingCommunicationsSource.id
            : null
        }
        patientId={patientId}
        onClose={handleCloseCommunicationsEdit}
        refetchList={handleCommunicationsEditSaved}
      />

      <ChangeStatusModal
        isOpen={isChangeStatusModalOpen}
        onClose={() => {
          setIsChangeStatusModalOpen(false);
          setEditingDocument(null);
        }}
        document={editingDocument}
        onSuccess={handleChangeStatusSuccess}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingDocument(null);
        }}
        document={deletingDocument}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
};

export { PatientDocumentsPage };
